// =================================================================
// commentsGenerator.js — Deterministic compositional generator.
//
// Every index in [0, TOTAL_COMMENTS) produces the SAME synthetic
// comment across renders, and the assembly guarantees uniqueness
// because it draws from at least 3 independent pools (intro, middle,
// reaction) whose combined space (~50 × 60 × 40 ≈ 120 000) is huge
// compared to the 1200 comments generated. Long-form comments add
// a second middle and an outro drawn from further independent pools.
//
//   Country distribution (100 buckets):
//     30% Mexico
//     50% USA — inner split 70% ES / 30% EN
//      8% Canada
//     10% Rest of Latin America
//      2% Outside Latin America
//
//   Service distribution (per client — can have 1..3 services):
//     Primary: 60% Complete Reading (15% Video)
//              15% Basic
//              20% Hechicería (90% Suprema/Poder · 10% Blanca/Negra)
//               2% Pactos · 3% Just Hired
//     Extras: 30% get a 2nd (different-category); 6% get a 3rd.
//     Pacto clients almost never combine hechicería.
//
//   Length distribution: 15% short · 40% med · 30% long · 15% xlong.
// =================================================================

import {
  COUNTRY_INFO, CITIES_BY_COUNTRY,
  US_FIRST_NAMES_EN, US_FIRST_NAMES_ES, US_LAST_NAMES,
  MX_FIRST_NAMES, MX_LAST_NAMES,
  CA_FIRST_NAMES, CA_LAST_NAMES,
  LATAM_FIRST_NAMES, LATAM_LAST_NAMES,
  NON_LATIN_FIRST_NAMES, NON_LATIN_LAST_NAMES,
  SVC_LABELS, CRIS_MENTIONS,
  MEMBERS_ES, MEMBERS_EN,
  INTROS_ES, MIDDLES_ES, REACTIONS_ES, OUTROS_ES,
  INTROS_EN, MIDDLES_EN, REACTIONS_EN, OUTROS_EN,
  JH_INTRO_ES, JH_MIDDLE_ES, JH_OUTRO_ES,
  JH_INTRO_EN, JH_MIDDLE_EN, JH_OUTRO_EN,
  CAT_ANCHORS_ES, CAT_ANCHORS_EN,
  SYSTEM_PRAISE_ES, SYSTEM_PRAISE_EN,
  SUFFIX_ES, SUFFIX_EN, TYPO_MAP_ES, TYPO_MAP_EN,
} from "@/data/commentsData";
import {
  detectGender,
  BODY_EMBARAZO, BODY_SALUD, BODY_QUIEBRA, BODY_DESAPARECIDO,
  BODY_PROTECCION, BODY_ADICCION, BODY_AMOR, BODY_MILAGRO,
  OPENINGS_ES_FEM, OPENINGS_ES_MASC, TRANSFORMATIONS_ES, CLOSINGS_ES,
  OPENINGS_EN_FEM, OPENINGS_EN_MASC,
  BODY_EMBARAZO_EN, BODY_SALUD_EN, BODY_QUIEBRA_EN, BODY_DESAPARECIDO_EN,
  BODY_PROTECCION_EN, BODY_ADICCION_EN, BODY_AMOR_EN, BODY_MILAGRO_EN,
  TRANSFORMATIONS_EN, CLOSINGS_EN,
  REGIONAL_FLAVOR,
  enumPick as emoPick, smallHash as emoHash,
} from "@/data/emotionalTestimonials";

const AVATAR_COLORS = [
  "bg-rose-500/30 text-rose-100 border-rose-400/40",
  "bg-amber-500/30 text-amber-100 border-amber-400/40",
  "bg-emerald-500/30 text-emerald-100 border-emerald-400/40",
  "bg-sky-500/30 text-sky-100 border-sky-400/40",
  "bg-violet-500/30 text-violet-100 border-violet-400/40",
  "bg-fuchsia-500/30 text-fuchsia-100 border-fuchsia-400/40",
  "bg-orange-500/30 text-orange-100 border-orange-400/40",
  "bg-teal-500/30 text-teal-100 border-teal-400/40",
];

const pick = (arr, idx) => arr[((idx % arr.length) + arr.length) % arr.length];
const h = (i, mul) => Math.abs((i * mul) | 0);
const between = (i, mul, min, max) => min + (h(i, mul) % Math.max(1, (max - min + 1)));

// =================================================================
// Country selection
// =================================================================
const COUNTRY_FOR_INDEX = (i) => {
  const bucket = i % 100;
  if (bucket < 30) return { style: "mx", country: "MX", lang: "es" };
  if (bucket < 80) {
    const inner = h(i, 3) % 10; // 70% ES / 30% EN
    if (inner < 7) return { style: "us_es", country: "US", lang: "es" };
    return { style: "us_en", country: "US", lang: "en" };
  }
  if (bucket < 88) return { style: "ca", country: "CA", lang: h(i, 5) % 3 === 0 ? "en" : "es" };
  if (bucket < 98) {
    // 14 Latam countries; multiplier must be coprime with 14 (so not
    // multiple of 2 nor 7). Using 9 gives a full rotation.
    const codes = ["AR", "CO", "CL", "PE", "VE", "EC", "DO", "CU", "PR", "GT", "CR", "PA", "UY", "BO"];
    return { style: "latam", country: pick(codes, h(i, 9)), lang: "es" };
  }
  const codes = ["ES", "UK", "IT", "FR", "DE", "AU", "BR"];
  const c = pick(codes, h(i, 11));
  return { style: "non_latin", country: c, lang: (c === "ES" || c === "BR") ? "es" : "en" };
};

// =================================================================
// Length selection
// =================================================================
const LENGTH_FOR_INDEX = (i) => {
  const b = (i + 7) % 100;
  if (b < 15) return "short";
  if (b < 55) return "med";
  if (b < 85) return "long";
  return "xlong";
};

// =================================================================
// Service selection with the 90/10 magic split enforced via ODD
// multipliers (see previous iteration bug notes).
// =================================================================
const pickBaseService = (i, salt) => {
  // Hash-based bucket instead of `(i + salt) % 100` — the linear form
  // was rigidly correlated with the country bucket (also `i % 100`),
  // making legit country×service combinations impossible (e.g. a
  // Mexican client getting Poder Máximo as primary).
  // salt values (13, 47, 59) are all coprime with 100, so `(salt*i) % 100`
  // cycles through the full 100 buckets and decorrelates from country.
  const b = h(i, salt) % 100;
  // All rolls of the form `h(i, X) % 100` MUST use a multiplier X
  // coprime with 100 (odd + not divisible by 5), otherwise the mod-100
  // range collapses onto only a few residues and probabilistic splits
  // stop working. `salt + N` for N = 100/200/300 preserves coprimality
  // when salt is coprime with 100 (which our salts are).
  if (b < 60) {
    if (h(i, salt + 100) % 100 < 15) return "lectura_completa_video";
    return "lectura_completa";
  }
  if (b < 75) return "lectura_basica";
  if (b < 95) {
    if (h(i, salt + 200) % 100 < 90) {
      const strongMagias = ["magia_suprema", "poder_maximo"];
      return pick(strongMagias, h(i, salt + 300));
    }
    const softMagias = ["magia_blanca", "magia_negra"];
    return pick(softMagias, h(i, salt + 400));
  }
  if (b < 97) {
    const pactos = ["pacto_luz", "pacto_satanico"];
    return pick(pactos, h(i, salt + 500));
  }
  return "__just_hired__";
};

const catOf = (svc) => SVC_LABELS[svc]?.cat || "reading";

const buildServices = (i) => {
  const primary = pickBaseService(i, 13);
  const justHired = primary === "__just_hired__";
  const primaryReal = justHired
    ? pick(["lectura_completa", "lectura_basica", "magia_suprema", "poder_maximo", "pacto_luz"], h(i, 29))
    : primary;
  const services = [primaryReal];
  const usedCats = new Set([catOf(primaryReal)]);

  if (!justHired) {
    const isPacto = catOf(primaryReal) === "pacto";
    const secondChance = isPacto ? 25 : 30;
    if ((h(i, 41) % 100) < secondChance) {
      const extra1 = pickBaseService(i, 47);
      const extra1Real = extra1 === "__just_hired__" ? "lectura_completa" : extra1;
      const extraCat = catOf(extra1Real);
      const compatible = !usedCats.has(extraCat) && (!isPacto || extraCat === "reading");
      if (compatible && extra1Real !== primaryReal) {
        services.push(extra1Real);
        usedCats.add(extraCat);
      }
    }
    if (!isPacto && (h(i, 53) % 100) < 6) {
      const extra2 = pickBaseService(i, 59);
      const extra2Real = extra2 === "__just_hired__" ? "lectura_basica" : extra2;
      const cat2 = catOf(extra2Real);
      if (!usedCats.has(cat2) && !services.includes(extra2Real)) {
        services.push(extra2Real);
        usedCats.add(cat2);
      }
    }
  }
  return { services, justHired };
};

// =================================================================
// Names & censoring
// =================================================================
const buildName = (loc, i) => {
  if (loc.style === "us_en") {
    return { first: pick(US_FIRST_NAMES_EN, h(i, 7)), middle: null,
             last1: pick(US_LAST_NAMES, h(i, 11)), last2: null };
  }
  if (loc.style === "us_es") {
    const two = h(i, 13) % 3 === 0;
    return {
      first: pick(US_FIRST_NAMES_ES, h(i, 17)),
      middle: two ? pick(US_FIRST_NAMES_ES, h(i, 19)) : null,
      last1: pick(US_LAST_NAMES, h(i, 23)),
      last2: two ? pick(US_LAST_NAMES, h(i, 29)) : null,
    };
  }
  if (loc.style === "mx") {
    return {
      first: pick(MX_FIRST_NAMES, h(i, 31)),
      middle: pick(MX_FIRST_NAMES, h(i, 37)),
      last1: pick(MX_LAST_NAMES, h(i, 41)),
      last2: pick(MX_LAST_NAMES, h(i, 43)),
    };
  }
  if (loc.style === "ca") {
    return {
      first: pick(CA_FIRST_NAMES, h(i, 47)),
      middle: h(i, 51) % 2 === 0 ? pick(CA_FIRST_NAMES, h(i, 53)) : null,
      last1: pick(CA_LAST_NAMES, h(i, 59)),
      last2: null,
    };
  }
  if (loc.style === "non_latin") {
    return {
      first: pick(NON_LATIN_FIRST_NAMES, h(i, 61)),
      middle: null,
      last1: pick(NON_LATIN_LAST_NAMES, h(i, 67)),
      last2: null,
    };
  }
  return {
    first: pick(LATAM_FIRST_NAMES, h(i, 71)),
    middle: h(i, 73) % 2 === 0 ? pick(LATAM_FIRST_NAMES, h(i, 79)) : null,
    last1: pick(LATAM_LAST_NAMES, h(i, 83)),
    last2: pick(LATAM_LAST_NAMES, h(i, 89)),
  };
};

const star = (word, keep) => {
  if (!word) return "";
  if (word.length <= keep) return word;
  return word.slice(0, keep) + "*".repeat(Math.max(3, word.length - keep));
};

const censorName = (n, loc) => {
  if (loc.country === "US" && loc.lang === "en") {
    return `${n.first}${n.middle ? " " + star(n.middle, 1) : ""} ${star(n.last1, 2)}${n.last2 ? " " + star(n.last2, 2) : ""}`;
  }
  if (loc.country === "CA" || loc.style === "non_latin") {
    return `${n.first}${n.middle ? " " + star(n.middle, 1) : ""} ${star(n.last1, 2)}`;
  }
  return `${n.first}${n.middle ? " " + star(n.middle, 1) : ""} ${star(n.last1, 1)}${n.last2 ? " " + star(n.last2, 1) : ""}`;
};

const initialsFor = (n) => ((n.first?.[0] || "?") + (n.last1?.[0] || (n.middle?.[0] || ""))).toUpperCase();

// =================================================================
// Time labels
// =================================================================
const timeLabel = (i, lang) => {
  const days = ((i * 11) % 720) + 1;
  if (lang === "en") {
    if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? "s" : ""} ago`;
    if (days < 60) return `1 month ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return "1 year ago";
  }
  if (days < 7) return `Hace ${days} día${days > 1 ? "s" : ""}`;
  if (days < 30) return `Hace ${Math.floor(days / 7)} sem.`;
  if (days < 60) return `Hace 1 mes`;
  if (days < 365) return `Hace ${Math.floor(days / 30)} meses`;
  return "Hace 1 año";
};

// =================================================================
// Impact score for likes
// =================================================================
const IMPACT_TRIGGERS = ["mamá", "mama", "papá", "papa", "hijo", "hija",
  "perdí", "perdi", "llor", "silencio", "duel", "difícil", "dificil",
  "mom", "dad", "cry", "lost", "grief", "child"];
const scoreImpact = (text, length) => {
  const lenScore = length === "xlong" ? 2 : length === "long" ? 1 : 0;
  const triggers = IMPACT_TRIGGERS.some((t) => text.toLowerCase().includes(t)) ? 1 : 0;
  return lenScore + triggers;
};

// =================================================================
// Style transforms (very sparingly used now — realism over noise)
// =================================================================
const maybeTypos = (text, i, isEn) => {
  const roll = (i * 13) % 100;
  const trigger = isEn ? 10 : 15;
  if (roll >= trigger) return text;
  const map = isEn ? TYPO_MAP_EN : TYPO_MAP_ES;
  const subs = 1;
  let out = text;
  for (let k = 0; k < subs; k++) {
    const pair = pick(map, h(i, 71 + k));
    out = out.replace(pair[0], pair[1]);
  }
  return out;
};

const maybeCase = (text, i, length) => {
  const roll = (i * 17) % 100;
  if (length === "xlong") {
    // xlong stays natural case for readability
    return text;
  }
  if (roll < 5) return text.toLowerCase();
  return text;
};

// =================================================================
// Compositional builder — assembles a testimonial from independent
// fragment pools indexed by a PRIME MULTIPLIER that is COPRIME with
// each pool's size. With pool sizes chosen as pairwise-coprime primes
// (INTROS 61, MIDDLES 71, REACTIONS 53, OUTROS 43), the combined
// enumeration period is 61×71×53×43 = 9,864,469 unique 4-tuples,
// which vastly exceeds 2400 comments — so every generated testimonial
// is GUARANTEED unique at the fragment-tuple level (no dupes by
// construction, not by hash probability).
//
// Multiplier choice (all coprime with their pool size):
//   intro    → mult 31 (coprime with 61)
//   middle1  → mult 37 (coprime with 71)
//   middle2  → mult 41 (coprime with 71)
//   reaction → mult 43 (coprime with 53)
//   reaction2→ mult 47 (coprime with 53)
//   outro    → mult 47 (coprime with 43)
//   anchor   → mult 53 (small pool ~11)
//   praise   → mult 59
// =================================================================
const substitute = (s, cris, city, member, days) => s
  .replaceAll("{cris}", cris)
  .replaceAll("{city}", city)
  .replaceAll("{member}", member)
  .replaceAll("{days}", String(days));

// Enumeration-based pool pick: index cycles through the pool using a
// multiplier coprime with the pool length, guaranteeing every position
// is hit exactly once within one cycle of `pool.length` consecutive i.
const enumPick = (pool, i, mult) => pool[((i * mult) % pool.length + pool.length) % pool.length];

const buildComment = (i) => {
  const loc = COUNTRY_FOR_INDEX(i);
  const length = LENGTH_FOR_INDEX(i);
  const name = buildName(loc, i);
  const isEn = loc.lang === "en";
  const { services, justHired } = buildServices(i);

  // Slot values (shared across all fragments assembled below)
  const cris = pick(CRIS_MENTIONS, h(i, 89));
  const days = between(i, 97, 5, 60);
  const cityList = CITIES_BY_COUNTRY[loc.country] || CITIES_BY_COUNTRY.MX;
  const city = pick(cityList, h(i, 101));
  const memberList = isEn ? MEMBERS_EN : MEMBERS_ES;
  const member = pick(memberList, h(i, 103));

  const intros = isEn ? INTROS_EN : INTROS_ES;
  const middles = isEn ? MIDDLES_EN : MIDDLES_ES;
  const reactions = isEn ? REACTIONS_EN : REACTIONS_ES;
  const outros = isEn ? OUTROS_EN : OUTROS_ES;

  // Just-hired: use enumeration on JH sub-pools.
  if (justHired) {
    const jhIntros = isEn ? JH_INTRO_EN : JH_INTRO_ES;
    const jhMids = isEn ? JH_MIDDLE_EN : JH_MIDDLE_ES;
    const jhOutros = isEn ? JH_OUTRO_EN : JH_OUTRO_ES;
    const parts = [
      substitute(enumPick(jhIntros, i, 31), cris, city, member, days),
      substitute(enumPick(jhMids, i, 37), cris, city, member, days),
      substitute(enumPick(jhOutros, i, 43), cris, city, member, days),
    ];
    return finalize(i, loc, name, length, parts.join(" "), services, justHired, isEn);
  }

  // Deterministic enumeration for the primary fragments — guarantees
  // that no two indices produce the same (intro, middle1, reaction)
  // tuple within the enumeration cycle (much larger than 2400).
  const intro = substitute(enumPick(intros, i, 31), cris, city, member, days);
  const middle1 = substitute(enumPick(middles, i, 37), cris, city, member, days);
  const reaction = substitute(enumPick(reactions, i, 43), cris, city, member, days);

  let text;
  if (length === "short") {
    text = `${intro} ${middle1} ${reaction}`;
  } else if (length === "med") {
    const middle2 = substitute(enumPick(middles, i, 41), cris, city, member, days);
    // With mults 37 and 41 both coprime with 71 but different, middle1 and
    // middle2 are essentially always different (would only collide if
    // (i*37) ≡ (i*41) mod 71, i.e. i*4 ≡ 0 mod 71, i.e. i ≡ 0 mod 71 —
    // handled by shifting to i+1).
    const m2 = middle2 === middle1
      ? substitute(enumPick(middles, i + 1, 41), cris, city, member, days)
      : middle2;
    text = `${intro} ${middle1} ${m2} ${reaction}`;
  } else if (length === "long") {
    const middle2 = substitute(enumPick(middles, i, 41), cris, city, member, days);
    const m2 = middle2 === middle1
      ? substitute(enumPick(middles, i + 1, 41), cris, city, member, days)
      : middle2;
    const outro = substitute(enumPick(outros, i, 47), cris, city, member, days);
    text = `${intro} ${middle1} ${m2} ${reaction} ${outro}`;
  } else {
    const middle2 = substitute(enumPick(middles, i, 41), cris, city, member, days);
    const m2 = middle2 === middle1
      ? substitute(enumPick(middles, i + 1, 41), cris, city, member, days)
      : middle2;
    const reaction2 = substitute(enumPick(reactions, i, 47), cris, city, member, days);
    const r2 = reaction2 === reaction
      ? substitute(enumPick(reactions, i + 1, 47), cris, city, member, days)
      : reaction2;
    const outro = substitute(enumPick(outros, i, 47), cris, city, member, days);
    text = `${intro} ${middle1} ${m2} ${reaction} ${r2} ${outro}`;
  }

  // Category anchor — ALWAYS added so the chip is grounded in the text
  // (except for xlong which is already dense enough).
  const catPriority = ["pacto", "hechiceria", "reading"];
  const primaryCat = catPriority.find((c) =>
    services.some((s) => (SVC_LABELS[s]?.cat === c))
  ) || "reading";
  if (length !== "xlong") {
    const anchorPool = isEn ? CAT_ANCHORS_EN[primaryCat] : CAT_ANCHORS_ES[primaryCat];
    if (anchorPool && anchorPool.length) {
      const anchor = substitute(enumPick(anchorPool, i, 53), cris, city, member, days);
      text = `${text} ${anchor}`;
    }
  }
  // System praise — appended to a subset (25%) of med/long comments only.
  if ((length === "med" || length === "long") && (h(i, 149) % 100) < 25) {
    const praisePool = isEn ? SYSTEM_PRAISE_EN : SYSTEM_PRAISE_ES;
    const praise = enumPick(praisePool, i, 59);
    text = `${text} ${praise}`;
  }

  return finalize(i, loc, name, length, text, services, justHired, isEn);
};

const finalize = (i, loc, name, length, rawText, services, justHired, isEn) => {
  // Optional short suffix on non-xlong comments — most are empty.
  let text = rawText;
  if (length !== "xlong") {
    const suffixArr = isEn ? SUFFIX_EN : SUFFIX_ES;
    const suffix = pick(suffixArr, h(i, 107));
    if (suffix) text = `${text}${suffix}`;
  }
  text = maybeTypos(text, i, isEn);
  text = maybeCase(text, i, length);

  // Likes weighted by emotional impact & length.
  const impact = scoreImpact(text, length);
  let likes;
  if (justHired) {
    likes = between(i, 113, 0, 8);
  } else if (impact >= 3) {
    likes = between(i, 113, 380, 980);
  } else if (impact === 2) {
    likes = between(i, 113, 180, 480);
  } else if (impact === 1) {
    likes = between(i, 113, 60, 240);
  } else if (length === "med") {
    likes = between(i, 113, 15, 90);
  } else {
    likes = between(i, 113, 3, 40);
  }

  const verified = h(i, 109) % 7 < 4;
  const countryInfo = COUNTRY_INFO[loc.country] || COUNTRY_INFO.MX;

  return {
    id: `c${i}`,
    name: censorName(name, loc),
    fullName: `${name.first}${name.middle ? " " + name.middle : ""} ${name.last1}${name.last2 ? " " + name.last2 : ""}`,
    initials: initialsFor(name),
    country: countryInfo,
    countryCode: loc.country,
    city: (CITIES_BY_COUNTRY[loc.country] || CITIES_BY_COUNTRY.MX)[0],
    time: timeLabel(i, isEn ? "en" : "es"),
    text,
    length,
    likes,
    verified,
    avatarColor: pick(AVATAR_COLORS, h(i, 127)),
    rating: (h(i, 131) % 20 === 0) ? 4 : 5,
    lang: isEn ? "en" : "es",
    services,
    serviceLabels: services.map((s) => (SVC_LABELS[s]?.[isEn ? "en" : "es"] || s)),
    serviceCategories: [...new Set(services.map((s) => SVC_LABELS[s]?.cat || "reading"))],
    justHired,
  };
};

// =================================================================
// EMOTIONAL TESTIMONIALS BUILDER (indices 2400..3399)
// 1000 rich, hand-crafted stories for Magia Suprema + Poder Máximo,
// with 8 archetypes, gender-aware openings, regional flavor and
// coprime-multiplier enumeration for guaranteed uniqueness.
// =================================================================

// 8 archetypes → primary service always Magia Suprema OR Poder Máximo.
const EMO_ARCHETYPES = ["embarazo", "salud", "quiebra", "desaparecido", "proteccion", "adiccion", "amor", "milagro"];

// Archetype → body pool (Spanish + English variants). Each pool is prime-sized.
const EMO_BODY_ES = {
  embarazo: BODY_EMBARAZO,
  salud: BODY_SALUD,
  quiebra: BODY_QUIEBRA,
  desaparecido: BODY_DESAPARECIDO,
  proteccion: BODY_PROTECCION,
  adiccion: BODY_ADICCION,
  amor: BODY_AMOR,
  milagro: BODY_MILAGRO,
};
const EMO_BODY_EN = {
  embarazo: BODY_EMBARAZO_EN,
  salud: BODY_SALUD_EN,
  quiebra: BODY_QUIEBRA_EN,
  desaparecido: BODY_DESAPARECIDO_EN,
  proteccion: BODY_PROTECCION_EN,
  adiccion: BODY_ADICCION_EN,
  amor: BODY_AMOR_EN,
  milagro: BODY_MILAGRO_EN,
};

// Country picker for emotional pool: 80% ES-speaking countries (MX heavy),
// 15% USA (mixed ES/EN), 5% others. Ratios chosen to feel authentic and
// keep the volume of hand-crafted EN pool manageable.
const EMO_COUNTRY_FOR_INDEX = (i) => {
  const b = i % 100;
  if (b < 50) return { style: "mx", country: "MX", lang: "es" };
  if (b < 65) {
    // US mix — 60% ES/40% EN
    const inner = emoHash(i, 7) % 10;
    if (inner < 6) return { style: "us_es", country: "US", lang: "es" };
    return { style: "us_en", country: "US", lang: "en" };
  }
  if (b < 80) {
    const codes = ["AR", "CO", "CL", "PE", "VE", "EC", "DO", "CU", "PR", "GT", "CR", "PA", "UY", "BO"];
    return { style: "latam", country: codes[emoHash(i, 11) % codes.length], lang: "es" };
  }
  if (b < 88) return { style: "ca", country: "CA", lang: emoHash(i, 13) % 4 === 0 ? "en" : "es" };
  if (b < 95) return { style: "us_es", country: "US", lang: "es" };
  const others = ["ES", "UK", "IT", "FR", "DE", "AU", "BR"];
  const c = others[emoHash(i, 17) % others.length];
  return { style: "non_latin", country: c, lang: (c === "ES" || c === "BR") ? "es" : "en" };
};

// Primary service — always Magia Suprema or Poder Máximo (per user request).
// 60/40 split biased toward Magia Suprema (more common). ~10% get a Poder Máximo
// PLUS a Magia Suprema follow-up (they sometimes escalate).
const _emoPrimaryService = (i) => {
  const roll = emoHash(i, 19) % 100;
  return roll < 60 ? "magia_suprema" : "poder_maximo";
};
const _emoServices = (i) => {
  const primary = _emoPrimaryService(i);
  const list = [primary];
  // 8% get a Lectura Completa as follow-up (they came back for a reading).
  if (emoHash(i, 23) % 100 < 8) list.push("lectura_completa");
  // 4% get a second hechicería (escalation from Suprema to Poder Máximo).
  if (primary === "magia_suprema" && emoHash(i, 29) % 100 < 4) list.push("poder_maximo");
  return list;
};

// Length: 60% long, 30% xlong, 10% med. Emotional testimonials are longer
// per user request ("has que la gran mayoría de comentarios sean mucho más largos").
const _emoLength = (i) => {
  const b = emoHash(i, 31) % 100;
  if (b < 10) return "med";
  if (b < 70) return "long";
  return "xlong";
};

// Build a full-body name (uses the same pools already imported for consistency).
const _emoBuildName = (loc, i) => {
  if (loc.style === "us_en") {
    return { first: pick(US_FIRST_NAMES_EN, emoHash(i, 37)), middle: null,
             last1: pick(US_LAST_NAMES, emoHash(i, 41)), last2: null };
  }
  if (loc.style === "us_es") {
    const two = emoHash(i, 43) % 3 === 0;
    return {
      first: pick(US_FIRST_NAMES_ES, emoHash(i, 47)),
      middle: two ? pick(US_FIRST_NAMES_ES, emoHash(i, 53)) : null,
      last1: pick(US_LAST_NAMES, emoHash(i, 59)),
      last2: two ? pick(US_LAST_NAMES, emoHash(i, 61)) : null,
    };
  }
  if (loc.style === "mx") {
    return {
      first: pick(MX_FIRST_NAMES, emoHash(i, 67)),
      middle: pick(MX_FIRST_NAMES, emoHash(i, 71)),
      last1: pick(MX_LAST_NAMES, emoHash(i, 73)),
      last2: pick(MX_LAST_NAMES, emoHash(i, 79)),
    };
  }
  if (loc.style === "ca") {
    return {
      first: pick(CA_FIRST_NAMES, emoHash(i, 83)),
      middle: emoHash(i, 89) % 2 === 0 ? pick(CA_FIRST_NAMES, emoHash(i, 97)) : null,
      last1: pick(CA_LAST_NAMES, emoHash(i, 101)),
      last2: null,
    };
  }
  if (loc.style === "non_latin") {
    return {
      first: pick(NON_LATIN_FIRST_NAMES, emoHash(i, 103)),
      middle: null,
      last1: pick(NON_LATIN_LAST_NAMES, emoHash(i, 107)),
      last2: null,
    };
  }
  return {
    first: pick(LATAM_FIRST_NAMES, emoHash(i, 109)),
    middle: emoHash(i, 113) % 2 === 0 ? pick(LATAM_FIRST_NAMES, emoHash(i, 127)) : null,
    last1: pick(LATAM_LAST_NAMES, emoHash(i, 131)),
    last2: pick(LATAM_LAST_NAMES, emoHash(i, 137)),
  };
};

// Sprinkle regional modismo — ~35% chance, appended into a random position.
const _emoRegionalize = (text, loc, i) => {
  if (emoHash(i, 139) % 100 >= 35) return text;
  const key = loc.style === "us_es" ? "US_ES" : loc.country;
  const pool = REGIONAL_FLAVOR[key] || REGIONAL_FLAVOR.DEFAULT;
  const flavor = pool[emoHash(i, 149) % pool.length];
  if (!flavor) return text;
  // Insert flavor after first sentence.
  const parts = text.split(". ");
  if (parts.length < 2) return `${text} — ${flavor}.`;
  return `${parts[0]}, ${flavor}. ${parts.slice(1).join(". ")}`;
};

// Emo-specific typo introduction — a bit heavier than the main pool
// per user request ("no todos tengan buena ortografía"). ~25% get 1-2
// deliberate light typos.
const _emoMaybeTypos = (text, i, isEn) => {
  const roll = emoHash(i, 151) % 100;
  if (roll >= 25) return text;
  const map = isEn ? TYPO_MAP_EN : TYPO_MAP_ES;
  const subs = 1 + (emoHash(i, 157) % 2); // 1 or 2 substitutions
  let out = text;
  for (let k = 0; k < subs; k++) {
    const pair = pick(map, emoHash(i, 163 + k));
    out = out.replace(pair[0], pair[1]);
  }
  return out;
};

const _emoBuildComment = (i) => {
  // Local emo-index: 0..999 for uniqueness across the emo cycle.
  const ei = i - 2400;
  const loc = EMO_COUNTRY_FOR_INDEX(ei);
  const isEn = loc.lang === "en";
  const name = _emoBuildName(loc, ei);
  const gender = detectGender(name.first);
  const services = _emoServices(ei);
  const length = _emoLength(ei);
  const archetype = EMO_ARCHETYPES[emoHash(ei, 167) % EMO_ARCHETYPES.length];
  const cris = pick(CRIS_MENTIONS, emoHash(ei, 173));
  const cityList = CITIES_BY_COUNTRY[loc.country] || CITIES_BY_COUNTRY.MX;
  const city = pick(cityList, emoHash(ei, 179));
  const memberList = isEn ? MEMBERS_EN : MEMBERS_ES;
  const member = pick(memberList, emoHash(ei, 181));

  // Pool picks — all with coprime multipliers wrt each pool's prime size.
  const openings = isEn
    ? (gender === "fem" ? OPENINGS_EN_FEM : OPENINGS_EN_MASC)
    : (gender === "fem" ? OPENINGS_ES_FEM : OPENINGS_ES_MASC);
  const body = isEn ? EMO_BODY_EN[archetype] : EMO_BODY_ES[archetype];
  const trans = isEn ? TRANSFORMATIONS_EN : TRANSFORMATIONS_ES;
  const closings = isEn ? CLOSINGS_EN : CLOSINGS_ES;

  const opening = emoPick(openings, ei, 41);
  const body1 = emoPick(body, ei, 17);
  const body2 = emoPick(body, ei + 3, 19); // shift index to avoid duplicate body1
  const trans1 = emoPick(trans, ei, 13);
  const closing = emoPick(closings, ei, 29);

  let text;
  if (length === "med") {
    text = `${opening} ${body1} ${trans1} ${closing}`;
  } else if (length === "long") {
    const trans2 = emoPick(trans, ei + 2, 11);
    const t2Final = trans2 === trans1 ? emoPick(trans, ei + 5, 11) : trans2;
    text = `${opening} ${body1} ${body2} ${trans1} ${t2Final} ${closing}`;
  } else {
    const trans2 = emoPick(trans, ei + 2, 11);
    const t2Final = trans2 === trans1 ? emoPick(trans, ei + 5, 11) : trans2;
    const body3 = emoPick(body, ei + 7, 23);
    const closing2 = emoPick(closings, ei + 4, 31);
    const c2Final = closing2 === closing ? emoPick(closings, ei + 9, 31) : closing2;
    text = `${opening} ${body1} ${body2} ${body3} ${trans1} ${t2Final} ${closing} ${c2Final}`;
  }

  // Substitute {city}/{member}/{cris}/{days} placeholders if any appear.
  text = text
    .replaceAll("{cris}", cris)
    .replaceAll("{city}", city)
    .replaceAll("{member}", member);

  text = _emoRegionalize(text, loc, ei);
  text = _emoMaybeTypos(text, ei, isEn);

  return finalize(i, loc, name, length, text, services, false, isEn);
};


// =================================================================
// Public API
// =================================================================
export const TOTAL_COMMENTS = 3400;

// Base generator (indices 0..2399): compositional 4-fragment builder.
// Emotional generator (indices 2400..3399): archetype-based long-form builder
//   for Magia Suprema + Poder Máximo with gender-aware pronouns.
const _buildForIndex = (i) => (i < 2400 ? buildComment(i) : _emoBuildComment(i));

export const getCommentSlice = (start, end) => {
  const out = [];
  const from = Math.max(0, start | 0);
  const to = Math.min(TOTAL_COMMENTS, end | 0);
  for (let i = from; i < to; i++) out.push(_buildForIndex(i));
  return out;
};

export const generateComments = (count = TOTAL_COMMENTS) => getCommentSlice(0, count);

// The list of countries currently represented in the dataset — used by
// the UI to render the country filter. Sorted by Spanish name.
export const COUNTRIES_IN_DATASET = Object.entries(COUNTRY_INFO)
  .map(([code, info]) => ({ code, name: info.name, flag: info.flag }))
  .sort((a, b) => a.name.localeCompare(b.name, "es"));

// The list of services offered — used by the UI to render the
// service filter.
export const SERVICES_FOR_FILTER = Object.entries(SVC_LABELS)
  .map(([key, info]) => ({ key, es: info.es, en: info.en, cat: info.cat }));
